import { ToolOfflineMetadata, OfflineDataEnvelope, FreshnessStatus } from './offlineDataTypes';

export const OFFLINE_TOOL_REGISTRY: ToolOfflineMetadata[] = [
  // Universal Calculators (100% Deterministic & Full Offline)
  {
    toolId: 'percentage-calc',
    title: 'Percentage Calculator',
    category: 'UNIVERSAL',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Zero network dependency. Pure deterministic percentage math.'
  },
  {
    toolId: 'discount-calc',
    title: 'Discount & Final Price Calculator',
    category: 'UNIVERSAL',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Calculates discounted prices, margins and tax add-ons locally.'
  },
  {
    toolId: 'margin-calc',
    title: 'Margin & Markup Calculator',
    category: 'UNIVERSAL',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Revenue, COGS, gross margin & markup ratios computed offline.'
  },
  {
    toolId: 'vat-calc',
    title: 'VAT & Sales Tax Calculator',
    category: 'UNIVERSAL',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Standard tax addition and subtraction with customizable local rates.'
  },
  {
    toolId: 'tip-split-calc',
    title: 'Tip & Bill Splitter',
    category: 'UNIVERSAL',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Fair split between multiple parties with customizable tip percentages.'
  },
  {
    toolId: 'work-hours-calc',
    title: 'Work Hours & Overtime Calculator',
    category: 'UNIVERSAL',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Daily timesheets, break deductions and overtime wage sums.'
  },
  {
    toolId: 'fuel-electricity-calc',
    title: 'Fuel & EV Charging Cost',
    category: 'UNIVERSAL',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Trip distance and consumption based on user-entered unit rates.'
  },
  {
    toolId: 'unit-converter-calc',
    title: 'Universal Unit Converter',
    category: 'UNIVERSAL',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Metric, imperial and scientific conversion constants stored locally.'
  },
  {
    toolId: 'date-age-calc',
    title: 'Date Difference & Exact Age',
    category: 'UNIVERSAL',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Calculates exact business days, leap years and age intervals.'
  },
  {
    toolId: 'health-wellness-calc',
    title: 'BMI & Daily Calorie Math',
    category: 'UNIVERSAL',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Standard WHO & Mifflin-St Jeor deterministic equations.'
  },

  // Wealth & Investment
  {
    toolId: 'savings-compound-interest',
    title: 'Compound Interest & Growth',
    category: 'WEALTH',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Deterministic compound interest, monthly contributions and tax drag.'
  },
  {
    toolId: 'future-present-value',
    title: 'Time Value of Money (PV/FV)',
    category: 'WEALTH',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Discount rate and annuity formulas computed entirely client-side.'
  },
  {
    toolId: 'fire-retirement-projection',
    title: 'FIRE & Retirement Runway',
    category: 'WEALTH',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Safe withdrawal rates (4% rule) and asset accumulation curves.'
  },
  {
    toolId: 'goal-planner-solver',
    title: 'Savings Goal Solver',
    category: 'WEALTH',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Monthly contribution required to reach financial milestone.'
  },

  // Loans & Mortgages
  {
    toolId: 'loan-engine',
    title: 'Mortgage & Loan Solver',
    category: 'LOAN',
    capability: 'FULL_OFFLINE',
    offlineDeterministic: true,
    offlineDescription: 'Exact French amortization schedules, principal and interest breakdowns.'
  },

  // Offline with Cached Verified Data
  {
    toolId: 'currency-intelligence',
    title: 'Currency Intelligence & FX',
    category: 'CURRENCY',
    capability: 'OFFLINE_WITH_CACHED_DATA',
    offlineDeterministic: true,
    requiredRulePack: 'fx-rates-snapshot-v1',
    lastVerifiedOnline: '2026-08-25T08:00:00Z',
    offlineDescription: 'Conversions use verified Last-Known-Good cached rates with clear timestamp.'
  },
  {
    toolId: 'salary-engine',
    title: 'Salary & Paycheck Tax Engine',
    category: 'SALARY',
    capability: 'OFFLINE_WITH_CACHED_DATA',
    offlineDeterministic: true,
    requiredRulePack: 'tax-brackets-2026-v2',
    lastVerifiedOnline: '2026-08-20T00:00:00Z',
    offlineDescription: 'Gross-to-net tax rules executed from cached official regulatory brackets.'
  },

  // Online Required (Live data or cloud AI required)
  {
    toolId: 'money-transfers',
    title: 'Live Money Transfer Comparison',
    category: 'TRANSFER',
    capability: 'ONLINE_REQUIRED',
    offlineDeterministic: false,
    offlineDescription: 'Live quotes, provider fees and delivery times require real-time network connection.'
  },
  {
    toolId: 'ai-search-assistant',
    title: 'YamooPay AI Assistant',
    category: 'AI',
    capability: 'ONLINE_REQUIRED',
    offlineDeterministic: false,
    offlineDescription: 'Large language model explanations require internet connectivity.'
  }
];

export class OfflineStorageManager {
  private static STORAGE_PREFIX = 'yamoopay_offline_';

  static saveOfflineEnvelope<T>(key: string, data: T, domain: string, freshness: FreshnessStatus = 'CACHED_VERIFIED'): OfflineDataEnvelope<T> {
    const envelope: OfflineDataEnvelope<T> = {
      id: `env-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      domain,
      dataVersion: '2026.08',
      source: 'YamooPay Canonical Engine',
      fetchedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(), // 7 days
      freshness,
      privacyClass: 'PUBLIC',
      cachePolicy: 'PUBLIC_FINANCIAL_REFERENCE',
      schemaVersion: 1,
      payload: data
    };

    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(this.STORAGE_PREFIX + key, JSON.stringify(envelope));
      }
    } catch (e) {
      console.warn('[OfflineEngine] Failed to write offline envelope to storage quota:', e);
    }

    return envelope;
  }

  static getOfflineEnvelope<T>(key: string): OfflineDataEnvelope<T> | null {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const item = localStorage.getItem(this.STORAGE_PREFIX + key);
        if (!item) return null;
        return JSON.parse(item) as OfflineDataEnvelope<T>;
      }
    } catch (e) {
      console.warn('[OfflineEngine] Failed to parse offline envelope:', e);
    }
    return null;
  }

  static clearAllOfflineFinancialData(): { clearedCount: number; timestamp: string } {
    let count = 0;
    if (typeof window !== 'undefined' && window.localStorage) {
      const keys = Object.keys(localStorage);
      for (const k of keys) {
        if (k.startsWith(this.STORAGE_PREFIX) || k.startsWith('yamoopay_draft_')) {
          localStorage.removeItem(k);
          count++;
        }
      }
    }

    // Also request Service Worker to clear runtime cache
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_FINANCIAL_CACHE' });
    }

    return {
      clearedCount: count,
      timestamp: new Date().toISOString()
    };
  }

  static getStorageEstimate(): Promise<{ usageMb: number; quotaMb: number; pctUsed: number }> {
    if (typeof navigator !== 'undefined' && 'storage' in navigator && navigator.storage.estimate) {
      return navigator.storage.estimate().then((est) => {
        const usageMb = Math.round(((est.usage || 0) / (1024 * 1024)) * 10) / 10;
        const quotaMb = Math.round(((est.quota || 1) / (1024 * 1024)) * 10) / 10;
        const pctUsed = Math.min(100, Math.round((usageMb / (quotaMb || 1)) * 100));
        return { usageMb, quotaMb, pctUsed };
      });
    }
    return Promise.resolve({ usageMb: 4.2, quotaMb: 500, pctUsed: 1 });
  }
}
